import { HAIRSP } from '../consts/characters';
import {
  FATHOM_TO_NAUTICAL_MILE,
  FOOT_TO_MILE,
  METER_TO_FATHOM,
  METER_TO_FOOT,
  METER_TO_KM,
  METER_TO_MILE,
  METER_TO_NAUTICAL_MILE,
} from '../consts/units';
import {
  elevationUnitByUnitType,
  intoMeters,
  paceUnitByUnitType,
  velocityUnitByUnitType,
} from '../functions/units';
import { floorTo } from '../roundTo';
import { type DistanceUnit, Unit, UnitType } from '../types/units';

export function getDistanceUnitLabel(unit: UnitType): string {
  switch (unit) {
    case UnitType.METRIC:
      return Unit.Distance.km;
    case UnitType.IMPERIAL:
      return Unit.Distance.mi;
    case UnitType.NAUTICAL:
      return Unit.Distance.nmi;
    default:
      return '';
  }
}

function distanceNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile' is not a valid Intl unit — caller must append the unit string manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'mile' : 'kilometer',
    style: showUnit ? 'unit' : 'decimal',
  };
}

export const labelFormatterDistance = (
  valueInMeterOrFootOrFathom: number | null | undefined,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = distanceNumberFormatOptions(unitType, digits, showUnit);

  if (valueInMeterOrFootOrFathom == null) {
    const unitString = !showUnit ? '' : getDistanceUnitLabel(unitType);
    return `-${HAIRSP}${unitString}`;
  }
  if (unitType === UnitType.IMPERIAL) {
    return `${(valueInMeterOrFootOrFathom * FOOT_TO_MILE).toLocaleString(undefined, options)}`;
  }
  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile' is not a valid Intl unit, so we append the unit string manually
    const formatted = (valueInMeterOrFootOrFathom * FATHOM_TO_NAUTICAL_MILE).toLocaleString(
      undefined,
      options,
    );
    return showUnit ? `${formatted}${HAIRSP}${Unit.Distance.nmi}` : formatted;
  }
  return `${(valueInMeterOrFootOrFathom * METER_TO_KM).toLocaleString(undefined, options)}`;
};

function elevationNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'fathom' is not a valid Intl unit — caller must append the unit string manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'foot' : 'meter',
    style: showUnit ? 'unit' : 'decimal',
  };
}

export const labelFormatterElevation = (
  valueInMeterOrFootOrFathom: number,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = elevationNumberFormatOptions(unitType, digits, showUnit);

  if (valueInMeterOrFootOrFathom == null)
    return `-${HAIRSP}${!showUnit ? '' : elevationUnitByUnitType(unitType)}`;

  if (unitType === UnitType.NAUTICAL) {
    // 'fathom' is not a valid Intl unit, so we append the unit string manually
    const formatted = valueInMeterOrFootOrFathom.toLocaleString(undefined, options);
    return showUnit ? `${formatted}${HAIRSP}${Unit.Elevation.ftm}` : formatted;
  }

  return valueInMeterOrFootOrFathom.toLocaleString(undefined, options);
};

function velocityNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile-per-hour' is not universally supported as an Intl unit — caller must append manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'mile-per-hour' : 'kilometer-per-hour',
    style: showUnit ? 'unit' : 'decimal',
  };
}

export const labelFormatterVelocity = (
  valueInKMHorMIHorKnot: number,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = velocityNumberFormatOptions(unitType, digits, showUnit);

  if (valueInKMHorMIHorKnot == null) {
    const unitString = !showUnit ? '' : velocityUnitByUnitType(unitType);
    return `-${HAIRSP}${unitString}`;
  }

  if (unitType === UnitType.NAUTICAL) {
    // 'nautical-mile-per-hour' is not universally supported as an Intl unit, so we append manually
    const formatted = valueInKMHorMIHorKnot.toLocaleString(undefined, options);
    return showUnit ? `${formatted}${HAIRSP}${Unit.Velocity.kn}` : formatted;
  }

  return valueInKMHorMIHorKnot.toLocaleString(undefined, options);
};

function paceNumberFormatOptions(
  unitType: UnitType,
  digits: number,
  showUnit: boolean,
): Intl.NumberFormatOptions {
  if (unitType === UnitType.NAUTICAL) {
    // 'minute-per-nautical-mile' is not a valid Intl unit — caller must append the unit string manually
    return { minimumFractionDigits: digits, maximumFractionDigits: digits, style: 'decimal' };
  }
  return {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    unit: unitType === UnitType.IMPERIAL ? 'minute-per-mile' : 'minute-per-kilometer',
    style: showUnit ? 'unit' : 'decimal',
  };
}

export const labelFormatterPace = (
  valueInMinPerKmOrMinPerMile: number,
  unitType: UnitType,
  digits = 2,
  showUnit = true,
) => {
  const options = paceNumberFormatOptions(unitType, digits, showUnit);

  if (valueInMinPerKmOrMinPerMile == null) {
    const unitString = !showUnit ? '' : paceUnitByUnitType(unitType);
    return `-${HAIRSP}${unitString}`;
  }

  if (unitType === UnitType.NAUTICAL) {
    // 'minute-per-nautical-mile' is not a valid Intl unit, so we append the unit string manually
    const formatted = valueInMinPerKmOrMinPerMile.toLocaleString(undefined, options);
    return showUnit ? `${formatted}${HAIRSP}${Unit.Pace.minPerNmi}` : formatted;
  }

  return valueInMinPerKmOrMinPerMile.toLocaleString(undefined, options);
};

interface FormatDistanceOptions {
  value: number | null | undefined;
  inputUnit: DistanceUnit;
  unitType: UnitType;
  digits?: number;
  maxLength?: number;
  padZeros?: boolean;
  useDecimalsBelow?: number;
}
/**
 * Formats a distance that’s given in any supported unit.
 * Internally converts to meters, then outputs either km (metric) or mi / ft (imperial).
 *
 * @param value – numeric distance in [inputUnit]
 * @param inputUnit – the unit of the provided distance (dm, m, km, mi, ft)
 * @param unitType – desired output system (UnitType.METRIC or UnitType.IMPERIAL or UnitType.NAUTICAL)
 * @param digits – decimal places for the formatted output (default: 2)
 * @param maxLength – max length of the numeric part (default: 7)
 * @param padZeros – if true, always show fixed decimals (e.g. 110.0); if false, no trailing zeros (default: false)
 * @param useDecimalsBelow - If provided, when |value| < threshold, decimal digits will be shown; otherwise rounded to integer.
 */
export function formatDistance({
  value,
  inputUnit,
  unitType,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatDistanceOptions): string {
  if (value == null || !Number.isFinite(value)) return '';
  const meters = intoMeters(value, inputUnit);

  if (unitType === UnitType.IMPERIAL) {
    // If the imperial “distance” is under 1 mile, we might want foot:
    const milesValue = meters * METER_TO_MILE;
    if (milesValue < 1) {
      return formatAsFoots(meters, digits);
    }
    return formatAsMiles({ meters, digits, maxLength, padZeros, useDecimalsBelow });
  }

  if (unitType === UnitType.NAUTICAL) {
    // If the nautical “distance” is under 1 nautical mile, we might want foot:
    const nauticalMilesValue = meters * METER_TO_NAUTICAL_MILE;
    if (nauticalMilesValue < 1) {
      return formatAsFathoms(meters, digits);
    }
    return formatAsNauticalMiles({ meters, digits, maxLength, padZeros, useDecimalsBelow });
  }

  // Metric: under 1 km → show meters; otherwise km
  const kmValue = meters * METER_TO_KM;
  if (kmValue < 1) {
    return formatAsMeters({ meters, digits, maxLength, padZeros, useDecimalsBelow });
  }
  return formatAsKM({ meters, digits, maxLength, padZeros, useDecimalsBelow });
}

/**
 * Helper function to format a numeric distance value already in “display units”.
 *
 * @param value - The distance value (already in km or miles) to format.
 * @param digits - The number of digits to use for floor rounding.
 * @param maxLength - The maximum allowed length of the numeric part.
 * @param suffix – unit suffix to append (e.g. "km", "mi", "m", "ft")
 * @param padZeros - If true, always show fixed decimals (e.g. 110.0);
 *                   if false, behave like before (no trailing zeros).
 * @param useDecimalsBelow - If provided, when |value| < threshold, decimal digits will be shown;
 *                           otherwise rounded to integer.
 * @returns A formatted string representing the numeric part.
 */
export function formatDistanceValueWithUnit(
  value: number,
  digits: number,
  maxLength: number,
  suffix: string,
  padZeros: boolean,
  useDecimalsBelow?: number,
): string {
  // Show decimals only when value is below a threshold
  let useFractional = true;
  if (useDecimalsBelow) {
    useFractional = Math.abs(value) < useDecimalsBelow;
  }

  const roundingDigits = useFractional ? digits : 0;
  const rounded = floorTo(value, roundingDigits);

  let valueStr: string;
  if (useFractional && roundingDigits > 0) {
    valueStr = padZeros ? rounded.toFixed(roundingDigits) : rounded.toString();
  } else {
    valueStr = rounded.toString();
  }

  if (valueStr.includes('.')) {
    if (valueStr.length > maxLength) {
      const integerPart = valueStr.split('.')[0];
      valueStr = integerPart.length <= maxLength ? integerPart : value.toExponential(2);
    }
  } else if (valueStr.length > maxLength) {
    valueStr = value.toExponential(2);
  }

  return `${valueStr}${HAIRSP}${suffix}`;
}

interface FormatUnitDistanceOptions {
  meters: number | null | undefined;
  digits?: number;
  maxLength?: number;
  padZeros?: boolean;
  useDecimalsBelow?: number;
}

export function formatAsKM({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const kmValue = meters / 1000;
  return formatDistanceValueWithUnit(
    kmValue,
    digits,
    maxLength,
    Unit.Distance.km,
    padZeros,
    useDecimalsBelow,
  );
}

export function formatAsMeters({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  return formatDistanceValueWithUnit(
    meters,
    digits,
    maxLength,
    Unit.Distance.m,
    padZeros,
    useDecimalsBelow,
  );
}

export function formatAsMiles({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const milesValue = meters * METER_TO_MILE;
  return formatDistanceValueWithUnit(
    milesValue,
    digits,
    maxLength,
    Unit.Distance.mi,
    padZeros,
    useDecimalsBelow,
  );
}

export function formatAsFoots(meters: number | null | undefined, digits = 2): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const footValue = meters * METER_TO_FOOT; //  3.2808;
  return `${floorTo(footValue, digits)}${HAIRSP}${Unit.Distance.ft}`;
}

export function formatAsNauticalMiles({
  meters,
  digits = 2,
  maxLength = 7,
  padZeros = false,
  useDecimalsBelow,
}: FormatUnitDistanceOptions): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const nauticalMilesValue = meters * METER_TO_NAUTICAL_MILE;
  return formatDistanceValueWithUnit(
    nauticalMilesValue,
    digits,
    maxLength,
    Unit.Distance.nmi,
    padZeros,
    useDecimalsBelow,
  );
}

export function formatAsFathoms(meters: number | null | undefined, digits = 2): string {
  if (meters == null || !Number.isFinite(meters)) return '';
  const fathomValue = meters * METER_TO_FATHOM;
  return `${floorTo(fathomValue, digits)}${HAIRSP}${Unit.Distance.ftm}`;
}

export function formatSpeed(
  offset: number | null | undefined,
  unitType: string,
  digits = 1,
): string {
  if (offset == null || !Number.isFinite(offset)) return '';
  const speed = offset * 3600;
  if (unitType === UnitType.IMPERIAL) {
    return `${floorTo(speed * 0.62137, digits)}${HAIRSP}${Unit.Velocity.mph}`;
  }
  if (unitType === UnitType.NAUTICAL) {
    return `${floorTo(speed * 0.539957, digits)}${HAIRSP}${Unit.Velocity.kn}`;
  }
  return `${floorTo(speed, digits)}${HAIRSP}${Unit.Velocity.kmh}`;
}
